/**
 * Grass Gone Wild — Responsive Smooth Scroll Physics Engine
 * Dynamically mounts on desktops and unmounts on mobile/touch screens.
 */

export class SmoothScroll {
  constructor() {
    this.container = document.getElementById('scroll-container');
    this.content = document.getElementById('scroll-content');
    
    if (!this.container || !this.content) return;
    
    this.scrollY = window.scrollY;
    this.currentY = window.scrollY;
    this.ease = 0.08; // Scroll inertia damping factor
    this.isScrolling = false;
    this.resizeObserver = null;
    this.scrollListener = null;
    this.isActive = false;

    this.checkResponsiveness();
    window.addEventListener('resize', () => this.checkResponsiveness());
  }

  checkResponsiveness() {
    // Enable smooth scroll only on non-touch desktop viewports wider than 900px
    const shouldBeActive = !('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth <= 900);

    if (shouldBeActive && !this.isActive) {
      this.activate();
    } else if (!shouldBeActive && this.isActive) {
      this.deactivate();
    }
  }

  activate() {
    this.isActive = true;
    document.body.classList.add('has-smooth-scroll');
    
    this.syncHeight();
    
    // Auto-update body heights on layout/resize expansions
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isActive) this.syncHeight();
    });
    this.resizeObserver.observe(this.content);
    
    // Register scroll event hooks
    this.scrollListener = () => {
      this.scrollY = window.scrollY;
      if (!this.isScrolling) {
        this.isScrolling = true;
        requestAnimationFrame(() => this.scrollLoop());
      }
    };
    window.addEventListener('scroll', this.scrollListener);
    
    // Neutralize container native scrolling shifts (hash jumps, layout jumps)
    this.containerScrollListener = () => {
      this.container.scrollTop = 0;
      this.container.scrollLeft = 0;
    };
    this.container.addEventListener('scroll', this.containerScrollListener);
    
    this.content.style.transform = `translate3d(0, -${this.currentY}px, 0)`;
    window.scrollCurrentY = this.currentY;
  }

  deactivate() {
    this.isActive = false;
    this.isScrolling = false;
    document.body.classList.remove('has-smooth-scroll');
    document.body.style.height = 'auto';
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
    
    if (this.containerScrollListener) {
      this.container.removeEventListener('scroll', this.containerScrollListener);
      this.containerScrollListener = null;
    }
    
    // Clear transformation styles to let native mobile scrolling flow
    this.content.style.transform = '';
    window.scrollCurrentY = undefined;
  }

  syncHeight() {
    const targetHeight = this.content.getBoundingClientRect().height;
    document.body.style.height = `${targetHeight}px`;
  }

  scrollLoop() {
    if (!this.isActive || !this.isScrolling) return;
    
    // Force native container offsets to 0 to prevent browser autofocus/jumping glitches
    this.container.scrollTop = 0;
    this.container.scrollLeft = 0;
    
    const delta = this.scrollY - this.currentY;
    
    if (Math.abs(delta) < 0.05) {
      this.currentY = this.scrollY;
      this.isScrolling = false;
    } else {
      this.currentY += delta * this.ease;
    }
    
    this.content.style.transform = `translate3d(0, -${this.currentY}px, 0)`;
    window.scrollCurrentY = this.currentY;
    
    // Dispatch scroll metrics for parallax trackers
    window.dispatchEvent(new CustomEvent('smooth-scroll', {
      detail: { 
        y: this.currentY, 
        targetY: this.scrollY,
        velocity: delta 
      }
    }));
    
    if (this.isScrolling) {
      requestAnimationFrame(() => this.scrollLoop());
    }
  }
}
